import { TransactionSearch, TransactionSearchKind } from "../../../types/transactions";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isUuid = (value: string): boolean => UUID_PATTERN.test(value.trim());

/**
 * Turn the search box into a server-side search: a UUID is looked up as a CDR ID, anything
 * else as a User ID, unless `kind` forces one. Blank input means no search.
 */
export const toSearch = (raw: string, kind?: TransactionSearchKind): TransactionSearch => {
  const value = (raw ?? "").trim();
  if (!value) return null;
  return { kind: kind ?? (isUuid(value) ? "cdr" : "user"), value };
};

export const otherSearchKind = (kind: TransactionSearchKind): TransactionSearchKind =>
  kind === "cdr" ? "user" : "cdr";

export const SEARCH_KIND_LABEL: Record<TransactionSearchKind, string> = {
  cdr: "CDR ID",
  user: "User ID",
};

export const pageCountOf = (total: number, pageSize: number): number =>
  Math.max(1, Math.ceil(total / pageSize));

const formatCount = (n: number) => n.toLocaleString("en-US");

const paymentsLabel = (n: number) => `${formatCount(n)} payment${n === 1 ? "" : "s"}`;

/**
 * The header line under "Payments":
 * - CDR lookup: "CDR lookup · 1 payment" / "CDR lookup · no payment", just "CDR lookup" while loading or failed;
 * - with a total: "Page 2 of ~13 · 1,234 payments";
 * - while counting: "Page 2 · counting payments…";
 * - count failed or unavailable: "Page 2".
 */
export const formatPaymentsSummary = ({
  pageNumber,
  pageSize,
  total,
  totalLoading,
  isCdrLookup,
  rowCount,
}: {
  pageNumber: number;
  pageSize: number;
  total: number | null;
  totalLoading: boolean;
  isCdrLookup: boolean;
  /** Rows on the page; null while loading or after a failed load (unknown, not zero). */
  rowCount: number | null;
}): string => {
  if (isCdrLookup) {
    if (rowCount === null) return "CDR lookup";
    return `CDR lookup · ${rowCount > 0 ? paymentsLabel(rowCount) : "no payment"}`;
  }
  if (total != null) {
    return `Page ${pageNumber} of ~${formatCount(pageCountOf(total, pageSize))} · ${paymentsLabel(total)}`;
  }
  if (totalLoading) return `Page ${pageNumber} · counting payments…`;
  return `Page ${pageNumber}`;
};
